import React, { useEffect, useState, useMemo } from 'react';
import { VariableSizeGrid as Grid } from 'react-window';
import { AutoSizer, MultiGrid } from 'react-virtualized';
import 'react-virtualized/styles.css'; // This is important!
import { useLazyQuery } from '@apollo/client';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { DashboardListReports, getDeletedUserHistory, listAllInteractions } from '../../../graphql/queries';
import { capitalize } from '../../../components/common/capitalize';
import { Bar } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import './Rank_Dashboard.css';
// Custom hook for normal (active) users
import useFetchUserByID from '../../../components/UserInfoLoader/findUserbyID';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function exportWeeklyDataToExcel(navigatorData, sortedNavigators, fridays) {
  const defaultStartDate = new Date('2025-08-27');
  const defaultEndDate = new Date('2026-08-29');
  const formatSheetData = (key) => {
    const rows = [];

    // Week range labels
    const fridays = getAllFridaysBetween(defaultStartDate, defaultEndDate);
    const weekRanges = fridays.map((friday, i) => {
      const start = friday.toISOString().split('T')[0];
      const nextFriday = fridays[i + 1];

      // If it's the last week, compute the real end (Thursday)
      const end = nextFriday
        ? new Date(new Date(nextFriday).getTime() - 1 * 24 * 60 * 60 * 1000)
        : getThursday(friday);

      const endStr = end.toISOString().split('T')[0];
      const nextStart = nextFriday
        ? nextFriday.toISOString().split('T')[0]
        : endStr; // doesn't matter since it's last week

      return { start, nextStart, label: `${start} - ${endStr}` };
    });

    // Header
    rows.push(['Navigator', ...weekRanges.map(w => w.label)]);

    for (const [userId, data] of sortedNavigators) {
      const row = [data.displayName];
      let rowSum = 0;

      for (const { start, nextStart } of weekRanges) {
        let sum = 0;

        if (data.dates) {
          for (const [dateStr, values] of Object.entries(data.dates)) {
            if (dateStr >= start && dateStr < nextStart) {
              sum += values[key] || 0;
            }
          }
        }

        rowSum += sum;
        row.push(sum);
      }

      // Validate against total
      const expectedTotal = data[key];
      if (rowSum !== expectedTotal) {
        console.warn(
          `Discrepancy for ${data.displayName} (${key}) — expected ${expectedTotal}, got ${rowSum}`
        );
      }

      rows.push(row);
    }

    return rows;
  };

  const qhpSheet = XLSX.utils.aoa_to_sheet(formatSheetData('QHP'));
  const medicaidSheet = XLSX.utils.aoa_to_sheet(formatSheetData('Medicaid'));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, qhpSheet, 'QHP');
  XLSX.utils.book_append_sheet(workbook, medicaidSheet, 'Medicaid');

  XLSX.writeFile(workbook, 'Navigator_Weekly_Report.xlsx');
}

// Helper: Parse a 'YYYY-MM-DD' string as local date at 00:00:00
function parseDateLocal(dateString) {
  // e.g. '2024-02-01' => new Date(2024, 1, 1, 0, 0, 0)
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

// Helper: Force the selected date to "Friday" start
function getFriday(d) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const day = date.getDay(); // Sunday=0, Monday=1, ... Saturday=6
  const shift = ((day - 5) + 7) % 7;
  date.setDate(date.getDate() - shift);
  return date;
}

// Helper: Friday + 6 days => Thursday
function getThursday(d) {
  const friday = getFriday(d);
  const thursday = new Date(friday.getTime() + (6 * 24 * 60 * 60 * 1000));
  thursday.setHours(23, 59, 59, 999);
  return thursday;
}

// Check if the navigator field looks like an ID (long string)
function isLikelyID(value) {
  return typeof value === 'string' && value.trim().length > 30;
}

function getAllFridaysBetween(startDate, endDate) {
  const fridays = [];
  let current = getFriday(startDate);
  while (current <= endDate) {
    fridays.push(new Date(current));
    current.setDate(current.getDate() + 7);
  }
  return fridays;
}

const RankDashboard = () => {
  // -------------------------------------------------
  // 1) States for data & loading
  // -------------------------------------------------
  const [allReports, setAllReports] = useState([]);
  const [navigatorData, setNavigatorData] = useState({});
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // -------------------------------------------------
  // 2) Selection logic
  // -------------------------------------------------
  const [selectedNavigators, setSelectedNavigators] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true); // only do "select all" once
  const ALLOWED_USER_GROUPS = ['Admin', 'Manager'];
  const userHasAccess = () => userGroups.some(group => ALLOWED_USER_GROUPS.includes(group));
  const [userEmail, setUserEmail] = useState(null);

  // -------------------------------------------------
  // 3) Time range toggles
  const [showByWeek, setShowByWeek] = useState(false);
  const [weekDate, setWeekDate] = useState(getFriday(new Date())); // 👈 default to Friday
  const defaultStartDate = new Date('2025-08-27');
  const defaultEndDate = new Date('2026-08-29');
  const weekStart = getFriday(weekDate);
  const weekEnd = getThursday(weekDate);

  // -------------------------------------------------
  // 4) Ranking Type
  // -------------------------------------------------
  const [rankingType, setRankingType] = useState('qhp');

  // -------------------------------------------------
  // 5) GQL queries for reports
  // -------------------------------------------------
  const [fetchReports] = useLazyQuery(DashboardListReports);
  const [fetchInteractions, { data: interactionsData, loading: loadingInteractions }] = useLazyQuery(listAllInteractions);
  // -------------------------------------------------
  // 6) Normal user (active) resolution
  // -------------------------------------------------
  const [nameMap, setNameMap] = useState({});
  const [navigatorQueue, setNavigatorQueue] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);

  // This custom hook fetches "active" user info by userId
  const currentID = navigatorQueue[currentIdx] || null;
  const { userInfo, loading: fetchingUser } = useFetchUserByID(currentID);

  // When userInfo arrives, store in nameMap
  useEffect(() => {
    if (!fetchingUser && userInfo && currentID) {
      const displayName = capitalize(userInfo.first_name) + ' ' + capitalize(userInfo.last_name);
      setNameMap((prev) => ({ ...prev, [currentID]: displayName }));
      setCurrentIdx((prev) => prev + 1);
    }
  }, [fetchingUser, userInfo, currentID]);

  // Once we finish fetching all IDs in normal queue, re-aggregate
  useEffect(() => {
    if (currentIdx >= navigatorQueue.length && navigatorQueue.length > 0) {
      processNavigatorData(allReports);
    }
    // eslint-disable-next-line
  }, [currentIdx, navigatorQueue]);

  // -------------------------------------------------
  // 6b) Deleted user resolution
  // -------------------------------------------------
  const [deletedNameMap, setDeletedNameMap] = useState({});
  const [deletedQueue, setDeletedQueue] = useState([]);
  const [deletedIdx, setDeletedIdx] = useState(0);

  const [fetchDeletedUser, { data: deletedUserData, loading: loadingDeleted }] = useLazyQuery(getDeletedUserHistory);


  const deletedID = deletedQueue[deletedIdx] || null;

  // Kick off a fetch for deleted user if we have an ID
  useEffect(() => {
    if (deletedID) {
      fetchDeletedUser({ variables: { id: deletedID } });
    }
  }, [deletedID, fetchDeletedUser]);

  // Store deleted user name once fetched
  useEffect(() => {
    if (!loadingDeleted && deletedUserData && deletedUserData.getDeletedUserHistory && deletedID) {
      const dUser = deletedUserData.getDeletedUserHistory;
      const displayName = capitalize(dUser.first_name) + ' ' + capitalize(dUser.last_name);
      setDeletedNameMap((prev) => ({ ...prev, [deletedID]: displayName }));
      setDeletedIdx((prev) => prev + 1);
    }
  }, [loadingDeleted, deletedUserData, deletedID]);

  // Once we finish the deleted user queue, re-aggregate
  useEffect(() => {
    if (deletedIdx >= deletedQueue.length && deletedQueue.length > 0) {
      processNavigatorData(allReports);
    }
    // eslint-disable-next-line
  }, [deletedIdx, deletedQueue]);

  // -------------------------------------------------
  // 7) Load All Reports once
  // -------------------------------------------------
  useEffect(() => {
    loadAllReports();
    // eslint-disable-next-line
  }, []);

  // Re-run aggregator whenever date toggles change
  useEffect(() => {
    if (allReports.length === 0) return;
    processNavigatorData(allReports);
    // eslint-disable-next-line
  }, [showByWeek, weekDate]);

  // Load all pages of reports
  const loadAllReports = async () => {
    let combinedReports = [];
    let nextToken = null;
    let isLoadingReports = true;

    setLoading(true);

    try {
      // Step 1: Load all reports (paginated)
      while (isLoadingReports) {
        const { data } = await fetchReports({
          variables: {
            limit: 200,
            nextToken,
          },
        });

        if (data?.listReportFixes?.items) {
          combinedReports = [...combinedReports, ...data.listReportFixes.items];
          nextToken = data.listReportFixes.nextToken;
        } else {
          nextToken = null;
        }
        isLoadingReports = !!nextToken;
      }

      setAllReports(combinedReports);

      // Step 2: Identify missing user IDs (deleted users)
      const unresolvedDeletedUserIDs = new Set();

      for (const report of combinedReports) {
        if (!report.user && isLikelyID(report.Navigator)) {
          unresolvedDeletedUserIDs.add(report.Navigator);
        }
      }

      // Step 3: Fetch all deleted users in parallel
      const deletedResults = await Promise.all(
        [...unresolvedDeletedUserIDs].map((id) =>
          fetchDeletedUser({ variables: { id } })
            .then((res) => {
              const user = res?.data?.getDeletedUserHistory;
              if (user) {
                return {
                  id,
                  displayName: capitalize(user.first_name) + ' ' + capitalize(user.last_name),
                };
              }
              return null;
            })
            .catch((err) => {
              console.warn(`Failed to fetch deleted user ${id}`, err);
              return null;
            })
        )
      );

      // Step 4: Build map of resolved deleted names
      const resolvedMap = {};
      for (const user of deletedResults) {
        if (user) {
          resolvedMap[user.id] = user.displayName;
        }
      }
      setDeletedNameMap(resolvedMap); // updates state used by processNavigatorData

      // Step 5: Build display data only after all names are resolved
      processNavigatorData(combinedReports);

    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };


  // -------------------------------------------------
  // 8) Aggregator Logic
  // -------------------------------------------------
  const processNavigatorData = (reportsArray) => {
    const startDate = showByWeek ? weekStart : defaultStartDate;
    const endDate = showByWeek ? weekEnd : defaultEndDate;

    const queueSet = new Set([...navigatorQueue]);
    const deletedSet = new Set([...deletedQueue]);

    // Key by userId or fallback name. 
    // For each key: { displayName, QHP, Medicaid }
    const navData = {};

    for (let report of reportsArray) {
      const reportDate = parseDateLocal(report.Date);
      if (reportDate < startDate || reportDate > endDate) continue;

      let userId = null;
      let displayName = null;

      if (report.user?.id) {
        userId = report.user.id;
        const first = report.user.first_name || '';
        const last = report.user.last_name || '';
        displayName = `${capitalize(first)} ${capitalize(last)}`.trim();
      } else if (report.Navigator) {
        userId = report.Navigator;
        const resolvedName = deletedNameMap[userId];
        if (resolvedName) {
          displayName = resolvedName;
        } else {
          queueSet.add(userId);
          continue; // Skip unresolved names
        }
      }

      if (!userId || !displayName) continue;

      if (!navData[userId]) {
        navData[userId] = { displayName, QHP: 0, Medicaid: 0 };
      }
      accumulate(navData[userId], report);
    }

    // update the queue states
    setNavigatorQueue(Array.from(queueSet));

    setNavigatorData(navData);

    // if first time => select all
    if (initialLoad) {
      setSelectedNavigators(Object.keys(navData));
      setInitialLoad(false);
    }
  };

  // Summation for QHP + Medicaid
  function accumulate(navObj, report) {
    const qhpFields = [
      'qhp_enrollment_hcgov',
      'elctronic_qhp_enrollment_marketplaceCallCenter',
      'written_qhp_enrollment_hcgov'
    ];

    const dateKey = report.Date; // "YYYY-MM-DD" from your DB (assuming it's stored that way)

    // If not existing, initialize a "dates" object
    if (!navObj.dates) {
      navObj.dates = {};
    }
    if (!navObj.dates[dateKey]) {
      navObj.dates[dateKey] = { QHP: 0, Medicaid: 0 };
    }

    // Sum QHP fields for *this* single report
    const qhpValue = qhpFields.reduce(
      (sum, field) => sum + (parseFloat(report[field]) || 0),
      0
    );
    const medicaidValue = parseFloat(report.medicaid_chip_app_ref) || 0;

    // 1) Add to this dateKey's totals
    navObj.dates[dateKey].QHP += qhpValue;
    navObj.dates[dateKey].Medicaid += medicaidValue;

    // 2) Also add to navigator's overall totals
    navObj.QHP += qhpValue;
    navObj.Medicaid += medicaidValue;
  }

  // -------------------------------------------------
  // 9) Sorting + Chart Data
  // -------------------------------------------------
  const sortedNavigatorsByQHP = Object.entries(navigatorData).sort(([, a], [, b]) => b.QHP - a.QHP);
  const sortedNavigatorsByMedicaid = Object.entries(navigatorData).sort(([, a], [, b]) => b.Medicaid - a.Medicaid);
  const sortedNavigatorsBySum = Object.entries(navigatorData).sort(([, a], [, b]) => (b.QHP + b.Medicaid) - (a.QHP + a.Medicaid));

  function getSortedNavigators(rankingType) {
    if (rankingType === 'qhp') return sortedNavigatorsByQHP;
    if (rankingType === 'medicaid') return sortedNavigatorsByMedicaid;
    return sortedNavigatorsBySum; // 'sum'
  }
  const currentSortedNavigators = useMemo(
    () => getSortedNavigators(rankingType),
    [navigatorData, rankingType]
  );

  function getColumnLabel(rankingType) {
    if (rankingType === 'qhp') return 'QHP';
    if (rankingType === 'medicaid') return 'Medicaid';
    return 'Total';
  }
  function getValueByType(data, rankingType) {
    if (rankingType === 'qhp') return data.QHP;
    if (rankingType === 'medicaid') return data.Medicaid;
    return data.QHP + data.Medicaid;
  }
  const columnLabel = getColumnLabel(rankingType);

  // Filter for QHP & Medicaid bar charts
  const filteredDataByQHP = sortedNavigatorsByQHP.filter(([userId]) => selectedNavigators.includes(userId));
  const filteredDataByMedicaid = sortedNavigatorsByMedicaid.filter(([userId]) => selectedNavigators.includes(userId));

  // Build chart data
  const qhpChartData = {
    labels: filteredDataByQHP.map(([userId]) => navigatorData[userId].displayName),
    datasets: [
      {
        label: 'QHP Enrollments',
        data: filteredDataByQHP.map(([, data]) => data.QHP),
        backgroundColor: 'rgba(39, 126, 223, 0.6)',
      },
    ],
  };

  const medicaidChartData = {
    labels: filteredDataByMedicaid.map(([userId]) => navigatorData[userId].displayName),
    datasets: [
      {
        label: 'Medicaid Enrollments',
        data: filteredDataByMedicaid.map(([, data]) => data.Medicaid),
        backgroundColor: 'rgba(44, 79, 163, 0.6)',
      },
    ],
  };

  const options = {
    scales: {
      y: { beginAtZero: true },
    },
  };

  // -------------------------------------------------
  // 10) Checkbox toggling
  // -------------------------------------------------
  const toggleNavigator = (userId) => {
    setSelectedNavigators((prevSelected) =>
      prevSelected.includes(userId)
        ? prevSelected.filter((nav) => nav !== userId)
        : [...prevSelected, userId]
    );
  };

  // -------------------------------------------------
  // 11) Toggle "All Time" vs "By Week"
  // -------------------------------------------------
  const handleShowByWeekChange = (e) => {
    setShowByWeek(e.target.value === 'byWeek');
  };

  const weekFridays = useMemo(
    () => getAllFridaysBetween(defaultStartDate, defaultEndDate),
    [defaultStartDate, defaultEndDate]
  );
  const weekLabels = useMemo(
    () => weekFridays.map((weekStart) => {
      const weekEnd = getThursday(weekStart);
      return `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
    }),
    [weekFridays]
  );
  const gridStyle = { '--week-count': weekLabels.length };


  // -------------------------------------------------
  // 12) Fetch User Details
  // -------------------------------------------------
  const fetchUserDetails = async () => {
    try {
      const { signInDetails } = await getCurrentUser();
      const session = await fetchAuthSession();
      const groups = session.tokens.idToken.payload['cognito:groups'] || [];
      setUserGroups(groups);
      setUserEmail(signInDetails.loginId.toLowerCase());
    } catch (error) {
      console.log('User not authenticated', error);
    }
  };
  useEffect(() => {
    fetchUserDetails();
  }, []);

  // -------------------------------------------------
  // RENDER
  // -------------------------------------------------
  return (
    <div className="rank-dashboard">
      <header className="rank-dashboard-header">
        <h1>Navigator Productivity Dashboard</h1>
      </header>

      {loading ? (
        <p>Loading data...</p>
      ) : (
        <>
          {/* Toggle: All Time vs By Week */}
          <div style={{ marginBottom: '1rem' }}>
            <label>
              <input
                type="radio"
                name="timeRange"
                value="allTime"
                checked={!showByWeek}
                onChange={handleShowByWeekChange}
              />
              All Time
            </label>
            &nbsp;&nbsp;
            <label>
              <input
                type="radio"
                name="timeRange"
                value="byWeek"
                checked={showByWeek}
                onChange={handleShowByWeekChange}
              />
              By Week
            </label>

            {showByWeek && (
              <div style={{ marginTop: '0.5rem' }}>
                <label htmlFor="weekDate">Select a date in the desired week:</label>
                <input
                  type="date"
                  id="weekDate"
                  value={weekDate.toISOString().split('T')[0]}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const [yyyy, mm, dd] = e.target.value.split('-');
                    setWeekDate(new Date(Number(yyyy), Number(mm) - 1, Number(dd), 12, 0, 0, 0));
                  }}
                />
                <p>
                  <strong>Week Range: </strong>
                  {weekStart.toDateString()} – {weekEnd.toDateString()}
                </p>
              </div>
            )}
          </div>

          {/* Navigator selection checkboxes */}
          <div className="navigator-selection">
            <button
              onClick={() => {
                if (selectedNavigators.length > 0) {
                  // If one or more navigators are selected, unselect them
                  setSelectedNavigators([]);
                } else {
                  // If none are selected, select them all
                  setSelectedNavigators(Object.keys(navigatorData));
                }
              }}
            >
              {selectedNavigators.length > 0 ? 'Remove All' : 'Select All'}
            </button>
            {Object.entries(navigatorData)
              .sort(([, a], [, b]) => (a.displayName || '').localeCompare(b.displayName || ''))
              .map(([userId, { displayName }]) => (
                <label key={userId} style={{ display: 'block' }}>
                  <input
                    type="checkbox"
                    checked={selectedNavigators.includes(userId)}
                    onChange={() => toggleNavigator(userId)}
                  />
                  <span style={{ fontFamily: 'monospace' }}>{displayName}</span>
                </label>
              ))}

          </div>

          {/* QHP Bar Chart */}
          <div className="chart-container">
            <h2>QHP Enrollments</h2>
            <Bar data={qhpChartData} options={options} />
          </div>

          {/* Medicaid Bar Chart */}
          <div className="chart-container">
            <h2>Medicaid Enrollments</h2>
            <Bar data={medicaidChartData} options={options} />
          </div>

          {/* Ranking Type Selector */}
          <div style={{ margin: '1rem 0' }}>
            <label>
              <input
                type="radio"
                name="rankingType"
                value="qhp"
                checked={rankingType === 'qhp'}
                onChange={(e) => setRankingType(e.target.value)}
              />
              QHP
            </label>
            &nbsp;&nbsp;
            <label>
              <input
                type="radio"
                name="rankingType"
                value="medicaid"
                checked={rankingType === 'medicaid'}
                onChange={(e) => setRankingType(e.target.value)}
              />
              Medicaid
            </label>
            &nbsp;&nbsp;
            <label>
              <input
                type="radio"
                name="rankingType"
                value="sum"
                checked={rankingType === 'sum'}
                onChange={(e) => setRankingType(e.target.value)}
              />
              Sum
            </label>
          </div>

          {/* Single Ranking Table */}
          <div className="rank-dashboard-tables-container">
            <h2 className="rank-dashboard-table-title">
              Ranking by {columnLabel}
            </h2>

            <table className="rank-dashboard-table rank-dashboard-single-table">
              <thead className="rank-dashboard-table-head">
                <tr>
                  <th className="rank-dashboard-table-header">Rank</th>
                  <th className="rank-dashboard-table-header">Navigator</th>
                  <th className="rank-dashboard-table-header">{columnLabel}</th>
                </tr>
              </thead>
              <tbody className="rank-dashboard-table-body">
                {currentSortedNavigators.map(([userId, data], index) => {
                  const { displayName } = data;
                  const value = getValueByType(data, rankingType);
                  return (
                    <tr className="rank-dashboard-table-row" key={userId}>
                      <td className="rank-dashboard-table-cell">{index + 1}</td>
                      <td className="rank-dashboard-table-cell">{displayName}</td>
                      <td className="rank-dashboard-table-cell">{value}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Weekly Summary Table */}
          {userHasAccess() && (
            <>
              <div style={{ margin: '1rem 0' }}>
                <button
                  onClick={() =>
                    exportWeeklyDataToExcel(navigatorData, currentSortedNavigators, getAllFridaysBetween(defaultStartDate, defaultEndDate))
                  }
                >
                  Export Weekly Report to Excel
                </button>
                <button
                  onClick={async () => {
                    try {
                      let allInteractions = [];
                      let nextToken = null;

                      do {
                        const { data } = await fetchInteractions({
                          variables: { nextToken },
                          fetchPolicy: 'network-only',
                        });

                        const items = data?.listInteractions?.items || [];
                        nextToken = data?.listInteractions?.nextToken || null;

                        allInteractions = [...allInteractions, ...items];
                      } while (nextToken);

                      if (allInteractions.length === 0) {
                        alert('No interactions found to export.');
                        return;
                      }

                      const worksheetData = allInteractions.map(i => ({
                        ID: i.id,
                        CallListID: i.callListID,
                        CallDate: i.callDate,
                        Time: i.time,
                        Answered: i.answered ? 'Yes' : 'No',
                        AppointmentMade: i.appointmentMade ? 'Yes' : 'No',
                        Notes: i.notes,
                        ContactMethod: i.contactMethod,
                        FollowUpScheduledDate: i.followUpScheduledDate,
                        CreatedBy: i.createdBy,
                        CreatedAt: i.createdAt,
                        UpdatedAt: i.updatedAt,
                      }));

                      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
                      const workbook = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(workbook, worksheet, 'Interactions');
                      XLSX.writeFile(workbook, 'All_Interactions.xlsx');
                    } catch (error) {
                      console.error('Error exporting interactions:', error);
                    }
                  }}
                >
                  Export All Interactions to Excel
                </button>
              </div>
              <div className="weekly-navigator-report">
                <h2 className="weekly-navigator-report__title">Weekly Navigator Report</h2>

                <div className="weekly-navigator-report__grid-container">
                  <div className="weekly-navigator-report__grid" style={gridStyle}>
                    {/* Header Row */}
                    <div className="weekly-navigator-report__cell weekly-navigator-report__cell--header weekly-navigator-report__cell--top-left">
                      Navigator
                    </div>
                    {weekLabels.map((label, index) => (
                      <div key={index} className="weekly-navigator-report__cell weekly-navigator-report__cell--header">
                        {label}
                      </div>
                    ))}

                    {/* Data Rows */}
                    {currentSortedNavigators.map(([userId, data], rowIndex) => (
                      <React.Fragment key={userId}>
                        {/* First Column (Navigator Name) */}
                        <div className="weekly-navigator-report__cell weekly-navigator-report__cell--first-col">
                          {data.displayName}
                        </div>

                        {/* Data Cells for the rest of the row */}
                        {weekLabels.map((_, colIndex) => {
                          const weekStart = weekFridays[colIndex];
                          const weekEnd = getThursday(weekStart);
                          let totalQHP = 0;
                          let totalMCD = 0;

                          if (data.dates) {
                            for (const [dateStr, values] of Object.entries(data.dates)) {
                              const d = parseDateLocal(dateStr);
                              if (d >= weekStart && d <= weekEnd) {
                                totalQHP += values.QHP || 0;
                                totalMCD += values.Medicaid || 0;
                              }
                            }
                          }

                          return (
                            <div key={`${userId}-${colIndex}`} className="weekly-navigator-report__cell">
                              {`QHP: ${totalQHP}, MCD: ${totalMCD}`}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )
      }
    </div >
  );
};

export default RankDashboard;
